/**
 * File Import Service
 * 
 * Handles processing of uploaded GMCT data files with proper validation,
 * categorization, and batch processing for knowledge base import.
 * 
 * Supports the GMCT data format with fields: url, title, content, timestamp
 */

import { chunkScrapedContent, type ContentChunk } from '../utils/chunkContent';

export interface GMCTDataItem {
  url: string;
  title: string;
  content: string;
  timestamp: string;
}

export interface KnowledgeChunk {
  content: string;
  metadata: {
    source: string;
    type: 'policy' | 'service' | 'location' | 'general' | 'pricing' | 'contact';
    category: string;
    original_url?: string;
    original_title?: string;
    original_timestamp?: string;
    processed_at: string;
    [key: string]: any;
  };
}

export interface ProcessingResult {
  success: boolean;
  total_chunks: number;
  processed: number;
  failed: number;
  errors: string[];
  batches?: Array<{
    batch: number;
    processed: number;
    failed: number;
    errors: string[];
  }>;
}

export interface ProgressCallback {
  (processed: number, total: number, status: string): void;
}

/**
 * Validates the structure of uploaded GMCT data
 */
export function validateGMCTData(data: any): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check if data is an array
  if (!Array.isArray(data)) {
    errors.push('Data must be an array of objects');
    return { isValid: false, errors };
  }

  // Check if array is not empty
  if (data.length === 0) {
    errors.push('Data array cannot be empty');
    return { isValid: false, errors };
  }

  // Validate each item in the array
  data.forEach((item, index) => {
    const itemErrors: string[] = [];

    // Check required fields
    if (!item.url || typeof item.url !== 'string') {
      itemErrors.push('url is required and must be a string');
    }

    if (!item.title || typeof item.title !== 'string') {
      itemErrors.push('title is required and must be a string');
    }

    if (!item.content || typeof item.content !== 'string') {
      itemErrors.push('content is required and must be a string');
    }

    if (!item.timestamp || typeof item.timestamp !== 'string') {
      itemErrors.push('timestamp is required and must be a string');
    }

    // Validate content length
    if (item.content && item.content.length < 10) {
      itemErrors.push('content must be at least 10 characters long');
    }

    // Validate timestamp format (basic check)
    if (item.timestamp && !isValidTimestamp(item.timestamp)) {
      itemErrors.push('timestamp must be a valid date string');
    }

    // Add item-specific errors
    if (itemErrors.length > 0) {
      errors.push(`Item ${index + 1}: ${itemErrors.join(', ')}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Basic timestamp validation
 */
function isValidTimestamp(timestamp: string): boolean {
  try {
    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Categorizes content based on keywords and context
 */
function categorizeGMCTContent(title: string, content: string, url: string): {
  category: string;
  type: 'policy' | 'service' | 'location' | 'general' | 'pricing' | 'contact';
} {
  const combinedText = `${title} ${content} ${url}`.toLowerCase();
  
  // Policy-related content
  if (combinedText.includes('policy') || combinedText.includes('rule') || 
      combinedText.includes('visiting hours') || combinedText.includes('permitted') ||
      combinedText.includes('guidelines') || combinedText.includes('must be') ||
      combinedText.includes('regulation') || combinedText.includes('restriction')) {
    return { category: 'policy', type: 'policy' };
  }
  
  // Service-related content
  if (combinedText.includes('service') || combinedText.includes('burial') || 
      combinedText.includes('cremation') || combinedText.includes('memorial') ||
      combinedText.includes('funeral') || combinedText.includes('pre-planning') ||
      combinedText.includes('ceremony') || combinedText.includes('chapel')) {
    return { category: 'services', type: 'service' };
  }
  
  // Location-related content
  if (combinedText.includes('location') || combinedText.includes('address') || 
      combinedText.includes('altona') || combinedText.includes('fawkner') ||
      combinedText.includes('emerald') || combinedText.includes('memorial park') ||
      combinedText.includes('cemetery') || combinedText.includes('directions')) {
    return { category: 'location', type: 'location' };
  }
  
  // Pricing-related content
  if (combinedText.includes('price') || combinedText.includes('cost') || 
      combinedText.includes('$') || combinedText.includes('fee') ||
      combinedText.includes('payment') || combinedText.includes('billing')) {
    return { category: 'pricing', type: 'pricing' };
  }
  
  // Contact-related content
  if (combinedText.includes('phone') || combinedText.includes('email') || 
      combinedText.includes('contact') || combinedText.includes('call') ||
      combinedText.includes('emergency') || combinedText.includes('support')) {
    return { category: 'contact', type: 'contact' };
  }
  
  // Default to general information
  return { category: 'general', type: 'general' };
}

/**
 * Extracts additional metadata from GMCT content
 */
function extractGMCTMetadata(item: GMCTDataItem): Record<string, any> {
  const metadata: Record<string, any> = {};
  
  // Extract phone numbers
  const phoneMatch = item.content.match(/\(?\d{2}\)?\s?\d{4}\s?\d{4}/g);
  if (phoneMatch) {
    metadata.phone_numbers = phoneMatch;
  }
  
  // Extract email addresses
  const emailMatch = item.content.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g);
  if (emailMatch) {
    metadata.email_addresses = emailMatch;
  }
  
  // Extract addresses
  const addressMatch = item.content.match(/\d+\s+[A-Za-z\s]+,\s+[A-Za-z\s]+\s+VIC\s+\d{4}/g);
  if (addressMatch) {
    metadata.addresses = addressMatch;
  }
  
  // Extract times/hours
  const timeMatch = item.content.match(/\d{1,2}(:\d{2})?\s*(AM|PM)/gi);
  if (timeMatch) {
    metadata.operating_hours = timeMatch;
  }
  
  // Extract prices
  const priceMatch = item.content.match(/\$[\d,]+(\.\d{2})?/g);
  if (priceMatch) {
    metadata.prices = priceMatch;
  }
  
  // Extract domain from URL
  try {
    const urlObj = new URL(item.url);
    metadata.domain = urlObj.hostname;
    metadata.url_path = urlObj.pathname;
  } catch {
    // Invalid URL, skip domain extraction
  }
  
  return metadata;
}

/**
 * Generates FAQ chunks for common cemetery questions
 */
function generateFAQChunks(): KnowledgeChunk[] {
  const faqs = [
    {
      question: "What are your visiting hours?",
      answer: "Our visiting hours are 9AM-5PM Monday-Friday and 9AM-4PM on weekends at Altona Memorial Park and Fawkner Memorial Park. Emerald Cemetery is open 9AM-4PM weekdays and 10AM-3PM weekends.",
      category: "faq",
      type: "policy" as const
    },
    {
      question: "How do I find a specific grave?",
      answer: "You can search for graves using our online search system or contact our office at (03) 8359 0111. Our staff can provide plot locations and directions to help you find your loved one's resting place.",
      category: "faq",
      type: "service" as const
    },
    {
      question: "Can I bring flowers to the cemetery?",
      answer: "Yes, fresh flowers are always welcome. Artificial flowers are permitted but must be in good condition. We remove wilted flowers during regular maintenance to keep the cemetery looking respectful.",
      category: "faq",
      type: "policy" as const
    },
    {
      question: "What cremation services do you offer?",
      answer: "We offer direct cremation starting from $1,800, cremation with memorial services from $2,500, and ash scattering in our memorial gardens. Our modern crematorium is located at Altona Memorial Park.",
      category: "faq",
      type: "service" as const
    },
    {
      question: "How much do burial plots cost?",
      answer: "Burial plot prices vary by location and type. Lawn burial plots start from $3,500, traditional burial plots from $4,200, and mausoleum spaces from $8,500. All prices include perpetual care.",
      category: "faq",
      type: "pricing" as const
    },
    {
      question: "Do you offer pre-planning services?",
      answer: "Yes, we offer comprehensive pre-planning services to help families make arrangements in advance. This reduces stress during difficult times and allows you to make informed decisions about your preferences.",
      category: "faq",
      type: "service" as const
    },
    {
      question: "What are your emergency contact details?",
      answer: "For emergencies, call our 24/7 emergency line at (03) 8359 0100. For general inquiries, contact our main office at (03) 8359 0111 or email info@gmct.com.au during business hours.",
      category: "faq",
      type: "contact" as const
    },
    {
      question: "Can I request maintenance for a grave site?",
      answer: "Yes, you can request maintenance through our online work request system or by calling (03) 8359 0111. We offer services like headstone cleaning, garden care, and memorial repairs.",
      category: "faq",
      type: "service" as const
    }
  ];

  return faqs.map((faq, index) => ({
    content: `Q: ${faq.question}\nA: ${faq.answer}`,
    metadata: {
      source: 'generated_faq',
      type: faq.type,
      category: faq.category,
      processed_at: new Date().toISOString(),
      faq_index: index,
      question: faq.question,
      answer: faq.answer
    }
  }));
}

/**
 * Converts GMCT data items to knowledge chunks
 */
function convertToKnowledgeChunks(data: GMCTDataItem[]): KnowledgeChunk[] {
  console.log('🔄 Converting GMCT data to knowledge chunks using intelligent chunking...');
  
  // Use the intelligent chunking utility
  const contentChunks: ContentChunk[] = chunkScrapedContent(data, {
    targetTokens: 400,
    overlapTokens: 50,
    minTokens: 200,
    maxTokens: 500,
    preserveSentences: true
  });
  
  console.log(`✅ Intelligent chunking complete: ${contentChunks.length} chunks created from ${data.length} items`);
  
  // Transform ContentChunk format to KnowledgeChunk format for API compatibility
  const knowledgeChunks: KnowledgeChunk[] = contentChunks.map((contentChunk, index) => ({
    content: contentChunk.content,
    metadata: {
      source: 'uploaded_file',
      type: contentChunk.metadata.type,
      category: contentChunk.metadata.category,
      original_url: contentChunk.metadata.source_url,
      original_title: contentChunk.metadata.title,
      original_timestamp: contentChunk.metadata.original_timestamp,
      processed_at: contentChunk.metadata.processed_at,
      content_length: contentChunk.content.length,
      token_count: contentChunk.metadata.token_count,
      chunk_index: contentChunk.metadata.chunk_index,
      total_chunks: contentChunk.metadata.total_chunks,
      // Include all extracted metadata from the chunking utility
      phone_numbers: contentChunk.metadata.phone_numbers,
      email_addresses: contentChunk.metadata.email_addresses,
      addresses: contentChunk.metadata.addresses,
      operating_hours: contentChunk.metadata.operating_hours,
      prices: contentChunk.metadata.prices,
      dates: contentChunk.metadata.dates,
      domain: contentChunk.metadata.domain,
      url_path: contentChunk.metadata.url_path,
      word_count: contentChunk.metadata.word_count,
      original_item_index: contentChunk.metadata.original_item_index
    }
  }));
  
  // Add FAQ chunks
  const faqChunks = generateFAQChunks();
  knowledgeChunks.push(...faqChunks);
  
  console.log(`📊 Final chunk count: ${knowledgeChunks.length} (${contentChunks.length} from content + ${faqChunks.length} FAQ)`);
  
  return knowledgeChunks.filter(chunk => chunk.content.length >= 10);
}

/**
 * Processes knowledge chunks in batches via API
 */
async function processChunksInBatches(
  chunks: KnowledgeChunk[],
  cemetery_id: string,
  onProgress?: ProgressCallback
): Promise<ProcessingResult> {
  const batchSize = 10;
  const results: ProcessingResult = {
    success: true,
    total_chunks: chunks.length,
    processed: 0,
    failed: 0,
    errors: [],
    batches: []
  };
  
  console.log(`🚀 Processing ${chunks.length} knowledge chunks in batches of ${batchSize}`);
  
  // Process in batches
  for (let i = 0; i < chunks.length; i += batchSize) {
    const batch = chunks.slice(i, i + batchSize);
    const batchNumber = Math.floor(i / batchSize) + 1;
    const totalBatches = Math.ceil(chunks.length / batchSize);
    
    console.log(`📦 Processing batch ${batchNumber}/${totalBatches} (${batch.length} chunks)`);
    
    if (onProgress) {
      onProgress(results.processed, chunks.length, `Processing batch ${batchNumber}/${totalBatches}...`);
    }
    
    try {
      const response = await fetch('/api/process-knowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cemetery_id,
          chunks: batch
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      const batchResult = {
        batch: batchNumber,
        processed: result.processed || 0,
        failed: result.failed || 0,
        errors: result.errors || []
      };
      
      results.processed += batchResult.processed;
      results.failed += batchResult.failed;
      results.errors.push(...batchResult.errors);
      results.batches!.push(batchResult);
      
      console.log(`✅ Batch ${batchNumber} complete: ${batchResult.processed} processed, ${batchResult.failed} failed`);
      
      // Add delay between batches to be respectful to the API
      if (i + batchSize < chunks.length) {
        console.log('⏳ Waiting 2 seconds before next batch...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
    } catch (error) {
      console.error(`❌ Batch ${batchNumber} failed:`, error);
      
      const batchResult = {
        batch: batchNumber,
        processed: 0,
        failed: batch.length,
        errors: [error instanceof Error ? error.message : 'Unknown batch error']
      };
      
      results.failed += batchResult.failed;
      results.errors.push(`Batch ${batchNumber}: ${batchResult.errors[0]}`);
      results.batches!.push(batchResult);
    }
  }
  
  // Determine overall success
  results.success = results.processed > 0;
  
  console.log(`🏁 Processing complete. Total processed: ${results.processed}, Failed: ${results.failed}`);
  
  return results;
}

/**
 * Main function to process uploaded GMCT data
 */
export async function processUploadedGMCTData(
  data: any,
  cemetery_id: string,
  onProgress?: ProgressCallback
): Promise<ProcessingResult> {
  try {
    console.log('🏗️ Starting GMCT uploaded data processing...');
    
    if (onProgress) {
      onProgress(0, 0, 'Validating uploaded data...');
    }
    
    // Validate data structure
    const validation = validateGMCTData(data);
    if (!validation.isValid) {
      console.error('❌ Data validation failed:', validation.errors);
      return {
        success: false,
        total_chunks: 0,
        processed: 0,
        failed: 0,
        errors: ['Data validation failed', ...validation.errors]
      };
    }
    
    console.log(`✅ Data validation passed for ${data.length} items`);
    
    if (onProgress) {
      onProgress(0, 0, 'Intelligently chunking content...');
    }
    
    // Convert to knowledge chunks using intelligent chunking
    const knowledgeChunks = convertToKnowledgeChunks(data);
    
    console.log(`🔄 Intelligently chunked ${data.length} items into ${knowledgeChunks.length} optimized chunks (including FAQ)`);
    console.log('📋 Chunk categories:', 
      [...new Set(knowledgeChunks.map(c => c.metadata.category))].join(', ')
    );
    console.log('📊 Token distribution:', {
      avg: knowledgeChunks.reduce((sum, chunk) => sum + (chunk.metadata.token_count || 0), 0) / knowledgeChunks.length,
      min: Math.min(...knowledgeChunks.map(c => c.metadata.token_count || 0)),
      max: Math.max(...knowledgeChunks.map(c => c.metadata.token_count || 0))
    });
    
    if (knowledgeChunks.length === 0) {
      return {
        success: false,
        total_chunks: 0,
        processed: 0,
        failed: 0,
        errors: ['No valid knowledge chunks could be created from the uploaded data']
      };
    }
    
    if (onProgress) {
      onProgress(0, knowledgeChunks.length, 'Processing optimized chunks...');
    }
    
    // Process chunks in batches
    const results = await processChunksInBatches(knowledgeChunks, cemetery_id, onProgress);
    
    console.log('🎉 GMCT intelligent chunking and processing complete!');
    console.log(`✅ Successfully processed: ${results.processed}`);
    console.log(`❌ Failed: ${results.failed}`);
    
    if (results.errors.length > 0) {
      console.log('⚠️ Errors encountered:');
      results.errors.forEach((error, index) => {
        console.log(`  ${index + 1}. ${error}`);
      });
    }
    
    if (onProgress) {
      onProgress(
        results.processed,
        knowledgeChunks.length,
        `Complete! ${results.processed} chunks imported, ${results.failed} failed`
      );
    }
    
    return results;
    
  } catch (error) {
    console.error('💥 GMCT intelligent chunking failed:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    if (onProgress) {
      onProgress(0, 0, `Failed: ${errorMessage}`);
    }
    
    return {
      success: false,
      total_chunks: 0,
      processed: 0,
      failed: 0,
      errors: [errorMessage]
    };
  }
}

/**
 * Utility function to get processing statistics
 */
export function getProcessingStats(result: ProcessingResult): {
  successRate: number;
  totalBatches: number;
  avgChunksPerBatch: number;
  errorRate: number;
} {
  const totalBatches = result.batches?.length || 0;
  const totalChunks = result.total_chunks;
  
  return {
    successRate: totalChunks > 0 ? (result.processed / totalChunks) * 100 : 0,
    totalBatches,
    avgChunksPerBatch: totalBatches > 0 ? totalChunks / totalBatches : 0,
    errorRate: totalChunks > 0 ? (result.failed / totalChunks) * 100 : 0
  };
}

/**
 * Utility function to format processing results for display
 */
export function formatProcessingResults(result: ProcessingResult): string {
  const stats = getProcessingStats(result);
  
  return `Processing completed:
• Total chunks: ${result.total_chunks}
• Successfully processed: ${result.processed} (${stats.successRate.toFixed(1)}%)
• Failed: ${result.failed} (${stats.errorRate.toFixed(1)}%)
• Batches processed: ${stats.totalBatches}
• Average chunks per batch: ${stats.avgChunksPerBatch.toFixed(1)}`;
}

/**
 * Export utility functions for external use
 */
export const fileImportUtils = {
  validateGMCTData,
  categorizeGMCTContent,
  extractGMCTMetadata,
  convertToKnowledgeChunks,
  getProcessingStats,
  formatProcessingResults
};